'use client'
import { formatCurrency } from '@/lib/utils'
import { SearchProductsToolResult, VespaPartResult } from '@/lib/utils/vespa'
import { Message as MessageType, useChat } from '@ai-sdk/react'
import { generateId } from 'ai'
import { usePostHog } from 'posthog-js/react'
import React, { useEffect, useRef, useState, useCallback } from 'react'
import { HiArrowUp } from 'react-icons/hi'
import { RiShareBoxLine } from 'react-icons/ri'
import Textarea from 'react-textarea-autosize'
import { useTheme } from '../lib/themeContext' // Import the theme context
import { Message } from './message'
import { CallbackRequestTimer } from './CallbackRequestTimer'
import { Chat as ChatType } from '@/app/lib/types'
import { CSProduct } from '@/lib/types'

// Define constants for user activity tracking
const ACTIVITY_TIMEOUT = 60000 // 60 seconds
const HEARTBEAT_INTERVAL = 30000 // 30 seconds

export function Chat({
  id,
  savedMessages,
  initialCurrentPrompts,
  initialShowFeedback = false,
  initialWasPromptClicked = false, // This is used for tracking to differentiate between users who clicked on a prompt and those who only use the input field
  readOnly = false,
  initialInput = null,
  productInfo = null,
  checkoutMode = false,
  getStartedMode = false,
  entryUrl,
  chat
}: {
  readOnly: boolean
  id: string
  savedMessages?: MessageType[]
  initialCurrentPrompts?: typeof questions
  initialShowFeedback?: boolean
  initialWasPromptClicked?: boolean
  initialInput?: string | null
  productInfo?: any | null
  checkoutMode?: boolean
  getStartedMode?: boolean
  entryUrl?: string | null
  chat: ChatType | null
}) {
  // Add online status tracking

  const isActiveRef = useRef(false)

  const userActivityTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const isLiveChat = chat?.isLiveChat || false
  const {
    primary,
    accent,
    secondary,
    questions,
    starterMessages,
    api,
    showCallButton,
    title,
    liveChatEnabled
  } = useTheme() // Consume the theme context
  const posthog = usePostHog()

  const [showInvestorRelationsButtons, setShowInvestorRelationsButtons] =
    useState(false)
  const [initialInputSubmitted, setInitialInputSubmitted] = useState(false)
  const checkoutInitialized = useRef(false)

  const [isCheckoutActive, setIsCheckoutActive] = useState(checkoutMode)

  const [llmCheckoutQuestion, setLlmCheckoutQuestion] = useState<string | null>(
    null
  )
  const [llmCheckoutButtons, setLlmCheckoutButtons] = useState<
    Array<{ text: string; value: string; nextActionHint?: string }>
  >([])

  // State to track callback requests and hide "Speak to Team Member" button temporarily
  const [callbackRequestUsed, setCallbackRequestUsed] = useState(false)
  const [messagesAfterCallback, setMessagesAfterCallback] = useState(0)

  const [actualInitialMessages] = useState<MessageType[]>(() => {
    if ((checkoutMode || getStartedMode) && productInfo) {
      return [
        {
          role: 'assistant',
          content: `Hey there I'm ${title}! I'm here to help you find exactly what you need to get started.`,
          id: generateId()
        },
        {
          role: 'assistant',
          content: 'Think of me as your personal concierge!',
          id: generateId()
        },
        {
          role: 'assistant',
          content: 'How would you like to get started today?',
          id: generateId()
        }
      ] as MessageType[]
    }
    return (savedMessages || starterMessages || []) as MessageType[]
  })

  const {
    messages: m,
    input,
    handleInputChange,
    handleSubmit,
    error,
    reload,
    setMessages,
    append,
    status,
    setInput,
    id: chatId
  } = useChat({
    api: api,
    id,
    // onError: error => {
    //   console.error(error)
    //   console.log(error.message)
    //   console.log(error.cause)
    //   console.log(error.name)
    // },
    //@ts-ignore
    initialMessages: actualInitialMessages,
    onToolCall: ({ toolCall }) => {
      if (toolCall.toolName === 'searchInvestorRelations') {
        setShowInvestorRelationsButtons(true)
      }
    },
    sendExtraMessageFields: true
  })

  const messages = isLiveChat && chat?.messages ? chat?.messages : m

  //@ts-ignore
  const firstRepMessage = messages.find(msg => msg.role === 'rep')

  // Store append in a ref to avoid dependency issues in useEffect
  const appendRef = useRef(append)
  useEffect(() => {
    appendRef.current = append
  }, [append])

  const ref = useRef<HTMLDivElement>(null)

  const [currentPrompts, setCurrentPrompts] = useState<
    { text: string; children?: any; parentText?: string; url?: string }[]
  >(
    !readOnly
      ? getStartedMode // Only show prompts in getStartedMode
        ? [
            { text: 'Start My Purchase' },
            { text: 'Get Financing' },
            { text: 'Submit a Trade In Request' },
            { text: 'Quick Question' }
          ]
        : checkoutMode
          ? [] // No prompts in checkoutMode
          : (initialCurrentPrompts ?? questions)
      : []
  )
  const [showFeedback, setShowFeedback] = useState(initialShowFeedback)
  const [wasPromptClicked, setWasPromptClicked] = useState(
    initialWasPromptClicked
  )

  // State to track if thinking indicator should be visible
  const [showThinkingIndicator, setShowThinkingIndicator] = useState(false)

  // Only update chatId in localStorage when user actually interacts with the chat
  // This prevents overwriting the previous chat ID when just viewing a new chat
  // If we have savedMessages, that means the user has already interacted with this chat
  const hasUserInteracted =
    messages.length > (actualInitialMessages?.length || 0) ||
    (savedMessages && savedMessages.length > 0)

  // Log when status changes
  useEffect(() => {
    // When submission starts, show the indicator
    if (status === 'submitted') {
      setShowThinkingIndicator(true)
    }

    // When chat returns to ready state, hide the indicator
    if (status === 'ready' || status === 'error') {
      setShowThinkingIndicator(false)
    }
  }, [status])

  // Check for text content in messages to hide the indicator when streaming starts
  useEffect(() => {
    // Don't run this check during non-streaming states
    if (status !== 'streaming') return

    const lastMessage = messages[messages.length - 1]
    if (lastMessage?.role === 'assistant') {
      // Check if there's substantial text content (at least 10 characters)
      const hasSubstantialTextContent = lastMessage.parts?.some(
        part =>
          part.type === 'text' && part.text && part.text.trim().length >= 10
      )

      // Only hide indicator when we have enough text content to display
      if (hasSubstantialTextContent) {
        setShowThinkingIndicator(false)
      }
    }
  }, [messages, status])

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTo({
        top: ref.current.scrollHeight,
        behavior: 'smooth'
      })
    }

    // Only update chat metadata in localStorage when user has actually interacted
    // This prevents mismatched data where lastChatMessage is from a new chat but chatId is from old chat
    if (hasUserInteracted) {
      localStorage.setItem('chatId', id)
      localStorage.setItem(
        'lastChatMessage',
        messages[messages.length - 1]?.content || ''
      )
      localStorage.setItem('lastChatDate', new Date().toISOString())
      // Store chat-specific state with chat ID as key suffix
      localStorage.setItem(
        `currentPrompts-${id}`,
        JSON.stringify(currentPrompts)
      )
      localStorage.setItem(`showFeedback-${id}`, JSON.stringify(showFeedback))
      localStorage.setItem(
        `promptClicked-${id}`,
        JSON.stringify(wasPromptClicked)
      )
    }
  }, [
    messages,
    status,
    hasUserInteracted,
    currentPrompts,
    showFeedback,
    wasPromptClicked,
    id
  ])

  useEffect(() => {
    // Update URL when checkout becomes active
    if (isCheckoutActive) {
      const currentUrl = new URL(window.location.href)
      // only update if not already set
      if (currentUrl.searchParams.get('checkout') !== 'true') {
        currentUrl.searchParams.set('checkout', 'true')
        if (productInfo?._id) {
          currentUrl.searchParams.set('csInventoryId', productInfo._id)
        }
        currentUrl.searchParams.delete('getstarted')
        window.history.pushState(
          { path: currentUrl.toString() },
          '',
          currentUrl.toString()
        )
      }
    }
  }, [isCheckoutActive, productInfo])

  async function customSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    event.stopPropagation()
    if (isLiveChat) {
      try {
        const response = await fetch('/api/chat/live/add-message', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chatId: id,
            message: input
          })
        })
        setInput('')
      } catch (error) {
        console.error('Error submitting live chat message:', error)
      }
    } else {
      // Show thinking indicator immediately when form is submitted
      setShowThinkingIndicator(true)

      if (error != null) {
        //@ts-ignore
        setMessages(messages.slice(0, -1)) // remove last message
      }
      // Clear LLM UI state on any custom submission
      setLlmCheckoutQuestion(null)
      setLlmCheckoutButtons([])

      setShowFeedback(true)
      setShowInvestorRelationsButtons(false)
      setCurrentPrompts([])
      handleSubmit(event)
    }
  }

  // Effect to handle automatic submission of initialInput
  useEffect(() => {
    if (
      initialInput &&
      !initialInputSubmitted &&
      !checkoutMode &&
      messages.length === actualInitialMessages.length
    ) {
      // Only submit if we haven't added any messages yet (only have initial messages)
      handleInputChange({
        target: { value: initialInput }
      } as React.ChangeEvent<HTMLTextAreaElement>)

      // Use setTimeout to ensure the input is set before submitting
      setTimeout(() => {
        const form = document.getElementById('chat-form') as HTMLFormElement
        if (form) {
          form.dispatchEvent(
            new Event('submit', { cancelable: true, bubbles: true })
          )
          setInitialInputSubmitted(true)
        }
      }, 100)
    }
  }, [
    initialInput,
    messages,
    actualInitialMessages,
    initialInputSubmitted,
    checkoutMode,
    handleInputChange
  ])

  // PARSE MESSAGES FOR LLM TOOL UI (askQuestionWithButtons, askFreeFormQuestion)
  useEffect(() => {
    const lastMessage = messages[messages.length - 1]

    if (
      lastMessage &&
      lastMessage.role === 'assistant' &&
      lastMessage.toolInvocations
    ) {
      let newQuestionCandidate: string | null = null
      let newButtonsCandidate: Array<{
        text: string
        value: string
        nextActionHint?: string
      }> | null = null
      let uiToolProcessed = false

      // First pass: look for askQuestionWithButtonsTool (priority)
      for (const toolInvocation of lastMessage.toolInvocations) {
        if (toolInvocation.toolName === 'askQuestionWithButtonsTool') {
          try {
            const args =
              typeof toolInvocation.args === 'string'
                ? JSON.parse(toolInvocation.args)
                : toolInvocation.args
            if (args.questionText && args.buttons) {
              newQuestionCandidate = args.questionText
              newButtonsCandidate = args.buttons
              uiToolProcessed = true
              break
            }
          } catch (e) {
            console.error(
              'Error parsing args for askQuestionWithButtonsTool:',
              e,
              toolInvocation.args
            )
          }
        }
      }

      // Second pass: only look for askFreeFormQuestionTool if no button tool was found
      if (!uiToolProcessed) {
        for (const toolInvocation of lastMessage.toolInvocations) {
          if (toolInvocation.toolName === 'askFreeFormQuestionTool') {
            try {
              const args =
                typeof toolInvocation.args === 'string'
                  ? JSON.parse(toolInvocation.args)
                  : toolInvocation.args
              if (args.questionText) {
                newQuestionCandidate = args.questionText
                newButtonsCandidate = [] // For free-form questions, buttons array is empty
                uiToolProcessed = true
                break
              }
            } catch (e) {
              console.error(
                'Error parsing args for askFreeFormQuestionTool:',
                e,
                toolInvocation.args
              )
            }
          }
        }
      }

      if (uiToolProcessed) {
        if (newQuestionCandidate !== llmCheckoutQuestion) {
          setLlmCheckoutQuestion(newQuestionCandidate)
        }
        const currentButtonsString = JSON.stringify(llmCheckoutButtons)
        const newButtonsString = JSON.stringify(newButtonsCandidate || [])
        if (newButtonsString !== currentButtonsString) {
          setLlmCheckoutButtons(newButtonsCandidate || [])
        }
        if (currentPrompts.length > 0) {
          setCurrentPrompts([])
        }
      }
    }
  }, [
    messages,
    llmCheckoutQuestion,
    llmCheckoutButtons,
    currentPrompts,
    isCheckoutActive
  ])

  // Track callback requests and messages after callback for API ignite
  useEffect(() => {
    // Only apply this logic for ignite API
    if (api !== '/api/ignite') return
    //console.log('messages', messages)
    // Find the most recent callback tool usage
    let mostRecentCallbackIndex = -1
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i]
      if (
        message.role === 'assistant' &&
        message.toolInvocations &&
        message.toolInvocations.some(
          toolInvocation =>
            toolInvocation.toolName === 'callbackRequest' ||
            toolInvocation.toolName === 'callbackRequestEquipment'
        )
      ) {
        mostRecentCallbackIndex = i
        break
      }
    }

    if (mostRecentCallbackIndex >= 0) {
      // Count user messages after the most recent callback
      const messagesAfterCallback = messages
        .slice(mostRecentCallbackIndex + 1)
        .filter(msg => msg.role === 'user').length

      setMessagesAfterCallback(messagesAfterCallback)

      // Show/hide button based on callback usage and message count
      if (messagesAfterCallback < 2) {
        setCallbackRequestUsed(true)
      } else {
        setCallbackRequestUsed(false)
      }
    } else {
      // No callback tools found, reset state
      setCallbackRequestUsed(false)
      setMessagesAfterCallback(0)
    }
  }, [messages, api])

  // Handler for LLM-driven checkout button clicks
  function handleLlmCheckoutButtonClick(button: {
    text: string
    value: string
    nextActionHint?: string
  }) {
    appendRef.current({
      role: 'user',
      content: button.text,
      id: generateId()
    })
    setLlmCheckoutQuestion(null)
    setLlmCheckoutButtons([])
    setCurrentPrompts([])

    posthog.capture('$checkout_button_clicked', {
      $chatId: id,
      buttonText: button.text,
      buttonValue: button.value,
      nextActionHint: button.nextActionHint
    })
  }

  // Simple function to send the checkout purchase message
  const sendCheckoutPurchaseMessage = useCallback(() => {
    if (checkoutInitialized.current || !productInfo) {
      return
    }

    appendRef.current({
      role: 'user',
      content: `Start My Purchase`,
      annotations: [
        {
          activeProduct: productInfo
        }
      ],
      id: generateId()
    })
    checkoutInitialized.current = true
  }, [productInfo])

  // Checkout initialization logic
  useEffect(() => {
    if (
      checkoutMode &&
      !checkoutInitialized.current &&
      status === 'ready' &&
      productInfo
    ) {
      const timer = setTimeout(() => {
        sendCheckoutPurchaseMessage()
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [checkoutMode, status, sendCheckoutPurchaseMessage, productInfo])

  // Send entry URL to metadata endpoint as soon as we have both values
  useEffect(() => {
    if (!entryUrl || !id) return

    fetch('/api/chat/metadata', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chatId: id,
        entryUrl: entryUrl
      })
    }).catch(err => console.error('Failed to save entry URL:', err))
  }, [entryUrl, id])

  // Add function to update online status in the database
  const updateOnlineStatus = useCallback(
    async (online: boolean) => {
      isActiveRef.current = online
      try {
        await fetch('/api/chat/status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chatId: id || chatId,
            isOnline: online,
            lastActive: new Date().toISOString()
          })
        })
      } catch (e) {
        console.error(e)
      }
    },
    [chatId, id]
  )

  // Track user activity and update online status
  const trackUserActivity = useCallback(() => {
    // mark active right away
    if (!isActiveRef.current) {
      updateOnlineStatus(true)
    }
    // reset "inactive" timer
    if (userActivityTimeoutRef.current) {
      clearTimeout(userActivityTimeoutRef.current)
    }
    userActivityTimeoutRef.current = setTimeout(() => {
      updateOnlineStatus(false)
    }, ACTIVITY_TIMEOUT)
  }, [updateOnlineStatus])

  // Set up activity tracking and heartbeat
  useEffect(() => {
    if (!hasUserInteracted) return

    // mark initial activity
    trackUserActivity()

    const events = ['mousedown', 'keypress', 'scroll', 'touchstart', 'focus']
    events.forEach(evt => window.addEventListener(evt, trackUserActivity))

    // set heartbeat
    heartbeatIntervalRef.current = setInterval(() => {
      if (isActiveRef.current) {
        updateOnlineStatus(true)
      }
    }, HEARTBEAT_INTERVAL)

    // make sure to go offline on unload
    window.addEventListener('beforeunload', () => updateOnlineStatus(false))

    return () => {
      events.forEach(evt => window.removeEventListener(evt, trackUserActivity))
      window.removeEventListener('beforeunload', () =>
        updateOnlineStatus(false)
      )
      if (userActivityTimeoutRef.current)
        clearTimeout(userActivityTimeoutRef.current)
      if (heartbeatIntervalRef.current)
        clearInterval(heartbeatIntervalRef.current)
      updateOnlineStatus(false)
    }
  }, [hasUserInteracted])

  function handlePromptClick(question: {
    text: string
    children?: any
    parentText?: string
    url?: string
  }) {
    posthog.capture('$promptClick', {
      $prompt: question.text,
      $chatId: id,
      ...(question?.parentText && {
        $parentPrompt: question.parentText
      })
    })

    setWasPromptClicked(true)
    setLlmCheckoutQuestion(null)
    setLlmCheckoutButtons([])

    if (question.url) {
      window.open(question.url, '_blank')
      return
    }

    const messageToSend: MessageType = {
      content: question.text,
      id: generateId(),
      role: 'user'
    }

    if (
      (getStartedMode || checkoutMode) &&
      question.text === 'Start My Purchase' &&
      productInfo
    ) {
      if (checkoutInitialized.current) return
      messageToSend.annotations = [{ activeProduct: productInfo }]
      checkoutInitialized.current = true
      setIsCheckoutActive(true) // Set state to trigger URL update
    }

    append(messageToSend)
    setCurrentPrompts(question.children || [])
  }

  // Update the Textarea component to track activity when typing
  return (
    <div
      className="relative mx-auto flex size-full h-full max-w-3xl flex-1 flex-col overflow-auto md:px-12"
      style={{ borderColor: primary }}
    >
      {isLiveChat && (
        <div className="flex items-center justify-center gap-2.5 bg-[#3C4346] py-[1px] text-xs text-white">
          Chatting with Live Agent
        </div>
      )}
      <div
        ref={ref}
        className="relative flex h-full flex-1 flex-col gap-2.5 overflow-auto px-3 py-4"
      >
        {/* {messages.map(({ message, direction }, index) => (
          <Message key={index} message={message} direction={direction} />
        ))} */}

        {messages?.map(message => {
          const hasCallbackRequest = message.toolInvocations?.some(
            t => t.toolName === 'callbackRequest' || t.toolName === 'createLead'
          )

          // Check if this message has a finalizeCheckoutTool call
          const hasFinalizeCheckout = message.toolInvocations?.some(
            t =>
              t.toolName === 'finalizeCheckoutTool' &&
              t.state === 'result' &&
              t.result?.success
          )

          const isFirstRepMessage = message.id === firstRepMessage?.id

          return (
            <React.Fragment key={message.id}>
              {(
                message.parts ||
                (Array.isArray(message.content) ? message.content : [])
              ).map(part => {
                switch (part.type) {
                  // render text parts as simple text:
                  case 'text':
                    if (part.text) {
                      if (liveChatEnabled && hasCallbackRequest) {
                        return null
                      }
                      // Don't render LLM text for checkout messages - we'll handle it in the tool case
                      if (hasFinalizeCheckout) {
                        return null
                      }
                      return (
                        <>
                          {isFirstRepMessage ? (
                            <div className="my-2 flex items-center gap-2.5 text-xs font-medium text-slate-400">
                              <div className="flex-1 border-t border-slate-400"></div>{' '}
                              <div>Chatting with Live Agent</div>
                              <div className="flex-1 border-t border-slate-400"></div>{' '}
                            </div>
                          ) : null}
                          <Message
                            message={part.text}
                            direction={
                              message.role === 'user' ? 'right' : 'left'
                            }
                          />
                        </>
                      )
                    } else {
                      return null
                    }

                  // for tool invocations, distinguish between the tools and the state:
                  case 'tool-invocation': {
                    switch (part.toolInvocation.toolName) {
                      case 'searchProducts': {
                        switch (part.toolInvocation.state) {
                          case 'result':
                            if (part.toolInvocation.result.length > 0) {
                              return (
                                <>
                                  <Message
                                    direction="left"
                                    message={`Here are some options available for you:`}
                                  />
                                  {part.toolInvocation.result.map(
                                    (product: SearchProductsToolResult) => {
                                      return (
                                        <ProductCard
                                          key={product.id}
                                          product={product}
                                          onClick={() => {
                                            posthog.capture(
                                              '$productCardClick',
                                              {
                                                $type: 'product',
                                                $product: product.id,
                                                $productUrl: product.productUrl,
                                                $chatId: id,
                                                $make: product.make,
                                                $model: product.model,
                                                $year: product.year,
                                                $stock: product.stockNumber
                                              }
                                            )
                                          }}
                                        />
                                      )
                                    }
                                  )}
                                </>
                              )
                            }
                        }
                        break
                      }
                      case 'searchParts': {
                        switch (part.toolInvocation.state) {
                          case 'result':
                            return part.toolInvocation.result
                              .slice(0, 3)
                              .map((part: any) => (
                                <PartCard
                                  key={part.id}
                                  part={part}
                                  onClick={() => {
                                    posthog.capture('$productCardClick', {
                                      $type: 'part',
                                      $product: part.id,
                                      $productUrl: part.url,
                                      $chatId: id,
                                      $partNumber: part.partNumber,
                                      $partTitle: part.title
                                    })
                                  }}
                                />
                              ))
                        }
                        break
                      }
                      case 'searchFitFinder': {
                        switch (part.toolInvocation.state) {
                          case 'result':
                            if (part.toolInvocation.result.length > 0) {
                              return (
                                <>
                                  <Message
                                    direction="left"
                                    message={`Here are some options available for you:`}
                                  />
                                  {part.toolInvocation.result.map(
                                    (product: SearchProductsToolResult) => {
                                      return (
                                        <ProductCard
                                          key={product.id}
                                          product={product}
                                          onClick={() => {
                                            posthog.capture(
                                              '$productCardClick',
                                              {
                                                $type: 'product',
                                                $product: product.id,
                                                $productUrl: product.productUrl,
                                                $chatId: id,
                                                $make: product.make,
                                                $model: product.model,
                                                $year: product.year,
                                                $stock: product.stockNumber
                                              }
                                            )
                                          }}
                                        />
                                      )
                                    }
                                  )}
                                </>
                              )
                            }
                        }
                        break
                      }
                      case 'careerDevelopment': {
                        switch (part.toolInvocation.state) {
                          case 'result':
                            return (
                              <a
                                target="_blank"
                                className="flex items-center gap-1 self-start rounded-lg bg-[#F3F3F3] p-2.5 text-sm font-semibold underline"
                                key={part.toolInvocation.toolCallId}
                                href={part.toolInvocation.result.link}
                                style={{ color: primary }}
                              >
                                {part.toolInvocation.result.text}
                                <RiShareBoxLine
                                  className="h-5 w-5"
                                  style={{
                                    color:
                                      primary === '#ffffff'
                                        ? '#808080'
                                        : primary
                                  }}
                                />
                              </a>
                            )
                        }
                        break
                      }

                      case 'callbackRequestEquipment':
                      case 'callbackRequest':
                      case 'createLead': {
                        switch (part.toolInvocation.state) {
                          case 'result':
                            return (
                              liveChatEnabled && (
                                <CallbackRequestTimer
                                  title={title}
                                  isLiveChat={isLiveChat}
                                  showCallButton={showCallButton}
                                  accent={accent}
                                  toolCallId={part.toolInvocation.toolCallId}
                                />
                              )
                            )
                        }
                        break
                      }
                      case 'finalizeCheckoutTool': {
                        switch (part.toolInvocation.state) {
                          case 'result':
                            if (
                              part.toolInvocation.result.success &&
                              part.toolInvocation.result.checkoutInfo
                            ) {
                              return (
                                <>
                                  <Message
                                    direction="left"
                                    message={part.toolInvocation.result.message}
                                  />
                                  <CheckoutCard
                                    checkoutInfo={
                                      part.toolInvocation.result.checkoutInfo
                                    }
                                    holdUrl={part.toolInvocation.result.holdUrl}
                                    primary={primary}
                                  />
                                  {status === 'ready' && (
                                    <Message
                                      direction="left"
                                      message="Is there anything else I can help you with today?"
                                    />
                                  )}
                                </>
                              )
                            }
                            return null
                        }
                        break
                      }
                    }
                  }
                }
              })}
            </React.Fragment>
          )
        })}

        {/* Render LLM Checkout Buttons */}
        {status === 'ready' && llmCheckoutButtons.length > 0 && (
          <div className="flex flex-col items-end gap-2 self-end py-2">
            {llmCheckoutButtons.map((button, index) => (
              <button
                key={`${button.text}-${index}-llm-checkout`}
                onClick={() => handleLlmCheckoutButtonClick(button)}
                className="self-end rounded-3xl border px-2.5 py-2 text-sm font-semibold shadow-md"
                style={{
                  borderColor: primary,
                  color: secondary
                }}
              >
                {button.text}
              </button>
            ))}
          </div>
        )}

        {status === 'ready' &&
          !llmCheckoutQuestion && // Only show if no LLM question
          llmCheckoutButtons.length === 0 && // Only show if no LLM buttons
          !isLiveChat && // Only show if not in live chat mode
          currentPrompts.map((question, index) =>
            question.url ? (
              <a
                target="_blank"
                className="flex items-center gap-1 self-start rounded-lg bg-[#F3F3F3] p-2.5 text-sm font-semibold underline"
                key={question.text}
                href={question.url}
              >
                {question.text}
                <RiShareBoxLine
                  className="h-5 w-5"
                  style={{
                    color: primary === '#ffffff' ? '#808080' : primary
                  }}
                />
              </a>
            ) : (
              <button
                key={question.text}
                onClick={() => handlePromptClick(question)}
                className="self-end rounded-3xl border px-2.5 py-2 text-sm font-semibold shadow-md"
                style={{
                  borderColor: primary === '#ffffff' ? '#000000' : primary,
                  color: secondary
                }}
              >
                {question.text}
              </button>
            )
          )}

        {error && (
          <>
            <div style={{ color: primary }}>An error occurred.</div>
            <button
              type="button"
              onClick={() => {
                posthog.capture('retry_click', { $chatId: id })
                // Clear LLM UI on retry
                setLlmCheckoutQuestion(null)
                setLlmCheckoutButtons([])
                return reload()
              }}
              style={{
                borderColor: primary,
                color: secondary
              }}
            >
              Retry
            </button>
          </>
        )}
        {!readOnly &&
          status === 'ready' &&
          showFeedback &&
          !llmCheckoutQuestion && // Only show if no LLM question
          llmCheckoutButtons.length === 0 && // Only show if no LLM buttons
          !showInvestorRelationsButtons &&
          !isLiveChat && (
            <div className="flex gap-2.5 self-end">
              <button
                onClick={() => {
                  posthog.capture('$thatHelped', {
                    $chatId: id
                  })
                  // Clear LLM UI when giving feedback
                  setLlmCheckoutQuestion(null)
                  setLlmCheckoutButtons([])
                  appendRef.current({
                    content: 'That Helped',
                    id: generateId(),
                    role: 'user'
                  })
                  setShowFeedback(false)
                }}
                className="self-end rounded-3xl border border-[#D9D9D9] px-2.5 py-2 text-sm font-semibold shadow-md"
              >
                That Helped
              </button>
              {!chat?.customerId &&
                !(
                  api === '/api/ignite' &&
                  callbackRequestUsed &&
                  messagesAfterCallback < 3
                ) && (
                  <button
                    onClick={() => {
                      posthog.capture('$speakToATeamMember', {
                        $chatId: id
                      })
                      // Clear LLM UI when giving feedback
                      setLlmCheckoutQuestion(null)
                      setLlmCheckoutButtons([])
                      appendRef.current({
                        content: 'Speak to Team Member',
                        id: generateId(),
                        role: 'user'
                      })
                      setShowFeedback(false)
                    }}
                    className="self-end rounded-3xl border border-[#D9D9D9] px-2.5 py-2 text-sm font-semibold shadow-md"
                  >
                    Speak to Team Member
                  </button>
                )}
            </div>
          )}
        {/* Buttons that link to the appropriate investor relations pages */}
        {status === 'ready' && showInvestorRelationsButtons && (
          <div className="flex flex-col gap-2.5 self-end">
            {[
              {
                text: 'Stock Information',
                href: 'https://ir.titanmachinery.com/Stock_Quote'
              },
              {
                text: 'Contact Investor Relations',
                href: 'https://ir.titanmachinery.com/Contact_IR'
              },
              {
                text: 'Events & Presentations',
                href: 'https://ir.titanmachinery.com/Events_Presentations'
              }
            ].map(button => (
              <a
                key={button.text}
                href={button.href}
                target="_blank"
                className="self-end rounded-3xl border border-[#D9D9D9] px-2.5 py-2 text-sm font-semibold shadow-md"
              >
                {button.text}
              </a>
            ))}
          </div>
        )}
        {showThinkingIndicator && <LoadingIndicator />}
      </div>
      {!readOnly && (
        <div className="sticky bottom-0 bg-white px-4 pb-12 lg:px-0">
          <form id="chat-form" onSubmit={customSubmit}>
            <div className="relative flex w-full items-center">
              <Textarea
                onChange={e => {
                  handleInputChange(e)
                  trackUserActivity() // Track activity when typing
                }}
                value={input}
                required
                name="input"
                rows={1}
                tabIndex={0}
                placeholder={
                  llmCheckoutQuestion && llmCheckoutButtons.length === 0
                    ? 'Your answer...'
                    : 'Message'
                }
                spellCheck={false}
                className="flex max-h-60 w-full resize-none rounded-3xl border border-input py-4 pl-4 pr-14 text-base leading-5 ring-offset-background placeholder:text-muted-foreground hover:shadow-lg focus-visible:shadow-lg focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                onKeyDown={e => {
                  // Enter should submit the form
                  if (e.key === 'Enter' && !e.shiftKey) {
                    // Prevent the default action to avoid adding a new line
                    e.preventDefault()
                    posthog.capture('$sendClick', {
                      $input: input,
                      $promptClicked: wasPromptClicked ? true : false,
                      $chatId: id
                    })
                    const textarea = e.target as HTMLTextAreaElement
                    textarea.form?.requestSubmit()
                  }

                  // Track activity on any key press
                  trackUserActivity()
                }}
                onFocus={() => {
                  trackUserActivity()
                }}
              />
              <button
                onClick={() => {
                  trackUserActivity()
                  posthog.capture('$sendClick', {
                    $input: input,
                    $promptClicked: wasPromptClicked ? true : false,
                    $chatId: id
                  })
                }}
                type="submit"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-2.5 text-xl"
                style={{
                  backgroundColor: primary === '#ffffff' ? accent : primary
                }}
              >
                <HiArrowUp className="text-white" />
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}

const ProductCard = ({
  product,
  onClick
}: {
  product: SearchProductsToolResult
  onClick: () => void
}) => {
  const { primary } = useTheme() // Consume the theme context
  return (
    <a
      href={product.productUrl}
      target="_blank"
      className="flex flex-col gap-1 rounded-lg bg-[#F3F3F3] p-2.5"
      onClick={onClick}
    >
      <div className="flex gap-1.5">
        <div>
          <img className="w-24" src={product.primaryImage} alt={product.make} />
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex items-center justify-between gap-1">
            <a
              href={product.productUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="text-sm font-semibold underline">
                {[product.year, product.make, product.model]
                  .filter(Boolean)
                  .join(' ')}
              </div>
            </a>
            <a
              href={product.productUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <RiShareBoxLine
                className="h-5 w-5"
                style={{ color: primary === '#ffffff' ? '#808080' : primary }}
              />
            </a>
          </div>
          <div className="text-sm font-semibold text-[#4F4F4F]">
            {product.price ? formatCurrency(product.price) : 'Call for Pricing'}
          </div>
          <div className="text-sm font-semibold text-[#4F4F4F]">
            {product.location}
          </div>
        </div>
      </div>
      <div className="line-clamp-2 text-sm text-[#4F4F4F]">
        {product.description}
      </div>
      <div className="text-sm font-semibold text-[#4F4F4F]">
        Stock # {product.dealerStockNumber}
      </div>
    </a>
  )
}

const PartCard = ({
  part,
  onClick
}: {
  part: VespaPartResult
  onClick: () => void
}) => {
  const { primary } = useTheme() // Consume the theme context
  return (
    <a
      target="_blank"
      href={part.url}
      className="flex flex-col gap-1 rounded-lg bg-[#F3F3F3] p-2.5"
      onClick={onClick}
    >
      <div className="flex gap-1.5">
        <div>
          <img className="w-24" src={part.primaryImage} alt={part.title} />
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex items-center justify-between gap-1">
            <a href={part.url} target="_blank" rel="noopener noreferrer">
              <div className="text-sm font-semibold underline">
                {part.title}
              </div>
            </a>
            <a href={part.url} target="_blank" rel="noopener noreferrer">
              <RiShareBoxLine
                className="h-5 w-5"
                style={{
                  color: primary === '#ffffff' ? '#808080' : primary
                }}
              />
            </a>
          </div>
          {part.price ? (
            <div className="text-sm font-semibold text-[#4F4F4F]">
              {formatCurrency(part.price)}
            </div>
          ) : null}
        </div>
      </div>
      <div className="line-clamp-2 text-sm text-[#4F4F4F]">
        {part.description}
      </div>
      <div className="text-sm font-semibold text-[#4F4F4F]">
        Part # {part.partNumber}
      </div>
    </a>
  )
}

const CheckoutCard = ({
  checkoutInfo,
  holdUrl,
  primary
}: {
  checkoutInfo: {
    currency: string
    validUntil: string
    productInfo: CSProduct
  }
  holdUrl: string
  primary: string
}) => {
  const formatValidDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    })
  }

  const productInfo = checkoutInfo.productInfo

  return (
    <a
      href={holdUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="flex cursor-pointer flex-col gap-1 rounded-lg bg-[#F3F3F3] p-2.5 transition-colors hover:bg-[#E8E8E8]"
    >
      {/* Product Information */}
      {productInfo && (
        <div className="flex gap-1.5">
          <div>
            <img
              className="w-24"
              src={productInfo.media[0].url || '/placeholder-product.jpg'}
              alt={
                `${productInfo.features.year || ''} ${productInfo.features.make || ''} ${productInfo.features.model || ''}`.trim() ||
                'Product'
              }
            />
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <div className="flex items-center justify-between gap-1">
              <div className="text-sm font-semibold">
                {[
                  productInfo.features.year,
                  productInfo.features.make,
                  productInfo.features.model
                ]
                  .filter(Boolean)
                  .join(' ')}
              </div>
              <RiShareBoxLine
                className="h-5 w-5"
                style={{
                  color: primary === '#ffffff' ? '#808080' : primary
                }}
              />
            </div>
            <div className="text-sm font-semibold text-[#4F4F4F]">
              {productInfo.price
                ? formatCurrency(productInfo.price)
                : 'Call for Pricing'}
            </div>
            <div className="text-sm font-semibold text-[#4F4F4F]">
              {productInfo.location.city}, {productInfo.location.state}
            </div>
            {productInfo.features.stockNumber && (
              <div className="text-sm font-semibold text-[#4F4F4F]">
                Stock# {productInfo.features.stockNumber}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stripe Secure Badge and Checkout Link */}
      <div className="mt-2 flex-col items-center gap-2">
        <div className="flex items-center gap-1">
          <span className="text-xs font-semibold text-[#DC2626]">
            Stripe Secure
          </span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 14 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.09184 7.73784L6.45578 9.16474L9.18367 6.31095M13.015 3.44574C10.8587 3.56554 8.74498 2.78552 7.13775 1.27686C5.53053 2.78552 3.41684 3.56554 1.26052 3.44574C1.08698 4.14857 0.999439 4.8716 1 5.5975C1 9.58639 3.60786 12.9389 7.13775 13.8892C10.6676 12.9389 13.2755 9.5871 13.2755 5.5975C13.2755 4.85408 13.1848 4.1335 13.015 3.44574Z"
              stroke="#DC2626"
              strokeWidth="1.36394"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="ml-auto">
          <span className="text-sm font-semibold text-[#DC2626] underline">
            Checkout Link
          </span>
        </div>
      </div>

      {/* Valid Until */}
      <div className="mt-1 text-xs text-[#4F4F4F]">
        Valid through: {formatValidDate(checkoutInfo.validUntil)}
      </div>
    </a>
  )
}

function LoadingIndicator() {
  return <div className="text-xs font-bold text-[#656C79]">Thinking...</div>
}
